import Axios, { AxiosResponse } from 'axios';
import React from 'react';
import { style } from 'typestyle';

import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { telemetryConsumerWrapper } from '~commonInfra/telemetryEvents/contexts/TelemetryConsumerWrapper';
import { ContentClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { ContentClickedEventHandler } from '~commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '~commonInfra/telemetryEvents/util/EventHandlerFactories';

import { Messages } from '../../../codegen/Localize';
import { ContentDraftStatus } from '../../../drafts/serverApi/types/ContentDraft';
import { DraftStyles } from '../../../drafts/styles/DraftStyles';
import { CardViewStyles } from './CardViewStyles';

export interface ThumbnailProps {
  readonly navigateUrl?: string; // If the navigateUrl is not present, then there will be no hand cursor or onclick action
  readonly defaultImageUrl: string;
  readonly allowDragAndDrop: boolean;
  readonly openLinkInNewTab?: boolean;
  readonly thumbnailImageUrl?: string;
  readonly imageCoverBackground?: boolean;
  readonly draftStatus?: string;
  readonly name?: string;
  readonly decorate?: boolean;
  readonly hoveredOrSelected?: boolean;
  readonly backgroundSize?: [number, number]; // X, Y px for background size (useful for SVGs)
  readonly useBase64EncodedImage?: boolean; // Use a base64 encoded svg data URI instead of an image
  readonly dontShowBorder?: boolean;
  readonly customHeight?: number;
  readonly allowFocusIndicator?: boolean;
  readonly centeredText?: string; // text to be rendered at the center of thumbnail
  readonly ariaLabel?: string; // aria label for the thumbnail link
}

export interface ThumbnailState {
  readonly loadThumbnailImage: boolean;
}

const CARD_STACK_DECORATION = <div className={CardViewStyles.ThumbnailWorkbookDecoration} />;

const focusIndicatorStyle: string = style({
  $nest: {
    '&:focus': CommonStyles.Focus.Indicator
  }
});

export class Thumbnail extends React.Component<ThumbnailProps, ThumbnailState> {
  private thumbnailImage: HTMLImageElement;
  private encodedSvg: string;

  constructor(props: ThumbnailProps) {
    super(props);
    this.state = { loadThumbnailImage: false };
    this.thumbnailImage = new Image();
  }

  public componentDidMount() {
    if (this.props.thumbnailImageUrl) {
      // If we are using a data uri, we need to fetch the content first
      if (this.props.useBase64EncodedImage) {
        Axios.get(this.props.thumbnailImageUrl)
          .then((result: AxiosResponse) => {
            this.encodedSvg = result.data;
            this.setState({loadThumbnailImage: true});
          });
      } else {
        this.thumbnailImage.src = this.props.thumbnailImageUrl;
        this.thumbnailImage.onload = () => {
          this.setState({ loadThumbnailImage: true });
        };
      }
    }
  }

  public componentWillUnmount() {
    this.thumbnailImage.onload = null;
  }

  private buildThumbnailOverlayDiv(): JSX.Element {
    return (
      <div data-tb-test-id={'thumbnail-overlay'}
        className={CardViewStyles.SelectOverlay}
        style={this.props.customHeight ? { height: this.props.customHeight + 'px' } : null}>
        {
          (this.props.draftStatus === ContentDraftStatus.unpublished) &&
          <div className={DraftStyles.DraftThumbnailTextStyle}>
            {Messages.draftThumbnailHoverTextForUnpublished()}
          </div>
        }
      </div>
    );
  }

  public render() {
    const notPreloadedClassNames = this.props.dontShowBorder ? CardViewStyles.ThumbnailNoBorder : CardViewStyles.Thumbnail;
    const classNames = this.state.loadThumbnailImage ? [CardViewStyles.ThumbnailPreloaded] : [notPreloadedClassNames];
    const imageUri = this.props.useBase64EncodedImage ? `data:image/svg+xml;base64,${this.encodedSvg}` : this.props.thumbnailImageUrl;
    const backgroundImageUrl = this.state.loadThumbnailImage ? imageUri : this.props.defaultImageUrl;

    if (this.props.hoveredOrSelected) {
      classNames.push(CardViewStyles.CardHighlight);
    }
    if (this.props.allowFocusIndicator) {
      classNames.push(focusIndicatorStyle);
    }

    const loadedImageOrCoverBackground = this.props.imageCoverBackground || this.state.loadThumbnailImage;

    const thumbnailStyle = {
      backgroundImage: `url(${backgroundImageUrl})`,
      backgroundSize: this.props.backgroundSize ? `${this.props.backgroundSize[0]}px ${this.props.backgroundSize[1]}px` : loadedImageOrCoverBackground ? 'cover' : '',
      height: this.props.customHeight ? this.props.customHeight + 'px' : null
    };

    return telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) => {
      return this.props.navigateUrl ?
        <div>
          <a href={this.props.navigateUrl}
            target={this.props.openLinkInNewTab ? '_blank' : null}
            rel="noopener noreferrer"
            /**
             * If card reordering allowed, disable link dragging.
             * Otherwise, set `draggable` explicitly to undefined rather than true.
             * (When `draggable` is true, the browser-provided drag avatar looks too similar
             * to the reorder card feature's avatar, which may confuse users.)
             */
            draggable={this.props.allowDragAndDrop ? false : undefined}
            onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
            className={classNames.join(' ')}
            style={thumbnailStyle}
            title={this.props.name}
            aria-label={this.props.ariaLabel}
          >
            {this.props.centeredText && <div className={CardViewStyles.ThumbnailCenteredText}>{this.props.centeredText}</div>}
            {this.buildThumbnailOverlayDiv()}
          </a>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div> :
        <div>
          <div
            className={classNames.join(' ')}
            style={thumbnailStyle}
            title={this.props.name}
            aria-label={this.props.ariaLabel}
          >
            {this.props.centeredText && <div className={CardViewStyles.ThumbnailCenteredText}>{this.props.centeredText}</div>}
            {this.buildThumbnailOverlayDiv()}
          </div>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div>;
    });
  }
}
